import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Sparkles, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Reveal, SectionLabel } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = {
  titan_starter: [
    "Up to 5 pages",
    "Mobile optimization",
    "Lead / contact system",
    "Analytics",
    "Technical SEO foundation",
    "Basic copy assistance",
  ],
  titan_growth: [
    "Up to 10 pages",
    "Conversion strategy",
    "Professional copy development",
    "SEO architecture",
    "CRM / calendar integration",
    "Analytics & conversion tracking",
    "Google Business integration",
  ],
  titan_premium: [
    "Custom architecture",
    "Advanced lead funnels",
    "E-commerce / booking",
    "CRM automation",
    "Advanced integrations",
    "Local SEO architecture",
    "Conversion optimization strategy",
  ],
};

const TAGLINES = {
  titan_starter: "Launch fast with a credible, modern presence.",
  titan_growth: "Everything a growing business needs to compound online.",
  titan_premium: "Own your category with a conversion engine built to scale.",
};

const PRICE_PREFIX = {
  titan_starter: "Starting at",
  titan_growth: "Starting at",
  titan_premium: "Starting at",
};

const money = (n) => `$${Math.round(n).toLocaleString()}`;

const Pricing = ({ onContact }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // { pkg }
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/payments/packages`)
      .then((r) => setPackages(r.data.packages || []))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, []);

  const startCheckout = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Please enter your name and email.");
    setSubmitting(true);
    try {
      const price = selected.pkg.full;
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: price.lookup_key,
        origin_url: window.location.origin,
        name: form.name,
        email: form.email,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  const featured = "titan_growth";

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-36 bg-[#050A14]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-8">
            <SectionLabel testid="pricing-label">Packages</SectionLabel>
            <Reveal>
              <h2 className="font-display font-bold tracking-tight text-white text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
                Straight-forward pricing. <span className="text-[#1E6BFF]">Zero surprises.</span>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-4">
            <Reveal delay={0.1}>
              <p className="text-gray-400 leading-relaxed">
                Fixed-price packages. One senior team, no monthly retainers, no hidden fees.
              </p>
            </Reveal>
          </div>
        </div>

        {loading ? (
          <div className="py-24 flex items-center justify-center text-gray-400">
            <Loader2 className="animate-spin mr-2" size={20} /> Loading packages…
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5">
            {packages.map((pkg, i) => {
              const isFeatured = pkg.id === featured;
              const price = pkg.full;
              return (
                <Reveal key={pkg.id} delay={i * 0.08}>
                  <motion.div
                    whileHover={{ y: -8 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    data-testid={`pricing-card-${pkg.id}`}
                    className={`relative h-full rounded-2xl p-8 flex flex-col ${
                      isFeatured
                        ? "border-2 border-[#1E6BFF] bg-gradient-to-b from-[#1E6BFF]/15 to-transparent glow-blue"
                        : "border border-white/10 bg-white/[0.03]"
                    }`}
                  >
                    {isFeatured && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-white bg-[#1E6BFF] px-3 py-1 rounded-full">
                        <Sparkles size={12} /> Most Popular
                      </span>
                    )}
                    <h3 className="font-display font-semibold text-2xl text-white">{pkg.name}</h3>
                    <p className="text-gray-400 text-sm mt-1 mb-5">{TAGLINES[pkg.id]}</p>

                    <div className="mb-6">
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Starting at</div>
                      <div className="flex items-baseline gap-1">
                        <span className="font-display font-bold text-5xl text-white">
                          {money(price.amount)}
                        </span>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-8 flex-1">
                      {(FEATURES[pkg.id] || []).map((f) => (
                        <li key={f} className="flex items-start gap-3 text-gray-200 text-sm">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${isFeatured ? "bg-[#1E6BFF]" : "bg-[#1E6BFF]/15 border border-[#1E6BFF]/30"}`}>
                            <Check size={12} className={isFeatured ? "text-white" : "text-[#1E6BFF]"} />
                          </span>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => setSelected({ pkg })}
                      data-testid={`buy-${pkg.id}`}
                      className={`w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold px-6 py-3.5 transition-colors duration-200 ${
                        isFeatured
                          ? "bg-[#1E6BFF] hover:bg-[#3B82F6] text-white"
                          : "border border-white/20 hover:border-white/50 text-white"
                      }`}
                    >
                      Get Started
                      <ArrowUpRight size={16} />
                    </button>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        )}

        <Reveal delay={0.2}>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-4 md:gap-6" data-testid="pricing-trust-row">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.03] px-4 py-2 text-xs text-gray-200">
              <span className="h-2 w-2 rounded-full bg-[#1E6BFF]" /> Fixed-price, no surprises
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.03] px-4 py-2 text-xs text-gray-200">
              <span className="h-2 w-2 rounded-full bg-[#1E6BFF]" /> Response in 24 hours
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <p className="text-center text-sm text-gray-500 mt-8">
            Not sure which package fits?{" "}
            <button onClick={onContact} className="text-[#1E6BFF] hover:underline" data-testid="pricing-plan-link">
              Get a free website plan
            </button>{" "}
            and we'll help you pick.
          </p>
        </Reveal>
      </div>

      {/* Checkout modal — collect email/name */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && !submitting && setSelected(null)}>
        <DialogContent data-testid="checkout-dialog" className="bg-[#0A1428]/95 backdrop-blur-2xl border border-[#1E6BFF]/40 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Get started with {selected?.pkg?.name}
            </DialogTitle>
            <DialogDescription className="text-gray-400 pt-1">
              Enter your details and we'll take you to secure checkout.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={startCheckout} className="space-y-4 pt-2">
            <Input
              data-testid="checkout-name"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-[#050A14]/60 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#1E6BFF] h-12"
              required
            />
            <Input
              data-testid="checkout-email"
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-[#050A14]/60 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#1E6BFF] h-12"
              required
            />
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-300 text-sm">{selected?.pkg?.name}</span>
                <span className="font-display font-bold text-white text-xl">
                  {selected ? money(selected.pkg.full.amount) : "$0"}
                </span>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="checkout-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] disabled:opacity-60 text-white font-semibold px-8 py-3.5 transition-colors duration-200 glow-blue"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Redirecting…
                </>
              ) : (
                "Continue to secure checkout"
              )}
            </button>
            <p className="text-center text-xs text-gray-500">Powered by Stripe · Test mode</p>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Pricing;
